const SELECTOR = '[data-kompac-hero-loop]';
const ACTIVE_ATTR = 'data-active';
const INITIALIZED_KEY = 'kompacHeroLoopInitialized';
const controllers = new WeakMap();

function asBoolean(value) {
  return value === true || value === 'true';
}

function toInteger(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mod(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

class KompacHeroLoopSlideshow {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-khls-viewport]');
    this.track = section.querySelector('[data-khls-track]');
    this.realSlides = this.track ? Array.from(this.track.querySelectorAll(':scope > [data-khls-slide]')) : [];
    this.dots = Array.from(section.querySelectorAll('[data-khls-dot]'));
    this.total = this.realSlides.length;
    this.current = Math.max(0, this.realSlides.findIndex((slide) => slide.hasAttribute(ACTIVE_ATTR)));
    this.displayIndex = this.total > 1 ? this.current + 1 : this.current;
    this.pendingCurrent = null;
    this.autoplay = asBoolean(section.dataset.autoplay);
    this.pauseOnHover = asBoolean(section.dataset.pauseOnHover);
    this.interval = Math.max(toInteger(section.dataset.interval, 5000), 1000);
    this.timer = null;
    this.pointerId = null;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.pointerDeltaX = 0;
    this.isDragging = false;
    this.isAnimating = false;
    this.transitionTimer = null;
    this.noMotionFrame = null;
    this.abortController = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.slides = [];
  }

  init() {
    if (!this.viewport || !this.track || this.total === 0) return;

    this.buildClones();
    this.updateActiveState();
    this.setTranslate(this.displayIndex, true);
    this.bindEvents();
    this.play();
    controllers.set(this.section, this);
  }

  buildClones() {
    this.track.querySelectorAll('[data-khls-clone]').forEach((clone) => clone.remove());

    if (this.total > 1) {
      const firstClone = this.realSlides[0].cloneNode(true);
      const lastClone = this.realSlides[this.total - 1].cloneNode(true);

      this.prepareClone(firstClone, 'tail');
      this.prepareClone(lastClone, 'head');

      this.track.insertBefore(lastClone, this.realSlides[0]);
      this.track.appendChild(firstClone);
    }

    this.slides = Array.from(this.track.querySelectorAll(':scope > [data-khls-slide]'));
  }

  prepareClone(clone, position) {
    clone.setAttribute('data-khls-clone', position);
    clone.removeAttribute(ACTIVE_ATTR);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
      node.setAttribute('tabindex', '-1');
    });
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(toInteger(dot.dataset.khlsDot, 0));
        this.restart();
      }, { signal });
    });

    this.section.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
        this.restart();
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
        this.restart();
      }
    }, { signal });

    this.section.addEventListener('pointerdown', (event) => {
      if (this.total <= 1 || this.isAnimating || event.button > 0) return;
      if (event.target.closest('a, button, input, select, textarea')) return;

      if (event.pointerType === 'mouse') event.preventDefault();

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerDeltaX = 0;
      this.isDragging = false;
      this.pause();
    }, { signal });

    this.section.addEventListener('pointermove', (event) => {
      if (this.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.isDragging && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 12) {
        this.resetPointer();
        this.play();
        return;
      }

      if (!this.isDragging && Math.abs(deltaX) < 8) return;

      event.preventDefault();
      this.isDragging = true;
      this.pointerDeltaX = deltaX;
      this.section.classList.add('khls--dragging');
      this.section.setPointerCapture?.(event.pointerId);
      this.setTranslate(this.displayIndex, true, deltaX);
    }, { signal });

    this.section.addEventListener('pointerup', (event) => {
      if (this.pointerId !== event.pointerId) return;

      const delta = this.pointerDeltaX || event.clientX - this.pointerStartX;
      const threshold = Math.min(this.section.clientWidth * 0.18, 120);
      this.section.releasePointerCapture?.(event.pointerId);
      this.section.classList.remove('khls--dragging');

      if (!this.isDragging || Math.abs(delta) < threshold) {
        this.setTranslate(this.displayIndex);
      } else {
        this.move(delta < 0 ? 1 : -1);
      }

      this.resetPointer();
      this.restart();
    }, { signal });

    this.section.addEventListener('pointercancel', (event) => {
      if (this.pointerId !== event.pointerId) return;

      this.section.classList.remove('khls--dragging');
      this.setTranslate(this.displayIndex);
      this.resetPointer();
      this.play();
    }, { signal });

    this.track.addEventListener('transitionend', (event) => {
      if (event.target !== this.track || event.propertyName !== 'transform') return;

      this.finishAnimation();
    }, { signal });

    window.addEventListener('resize', () => {
      if (this.isDragging) return;

      this.setTranslate(this.displayIndex, true);
    }, { signal });

    if (this.pauseOnHover) {
      this.section.addEventListener('pointerenter', () => this.pause(), { signal });
      this.section.addEventListener('pointerleave', () => this.play(), { signal });
      this.section.addEventListener('focusin', () => this.pause(), { signal });
      this.section.addEventListener('focusout', () => this.play(), { signal });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.play();
        this.updateVideoState();
      }
    }, { signal });
  }

  goTo(index) {
    if (this.isAnimating || this.total <= 1) return;

    const target = mod(index, this.total);
    if (target === this.current) return;

    const candidates = [target + 1, target + 1 - this.total, target + 1 + this.total]
      .filter((displayIndex) => displayIndex >= 0 && displayIndex <= this.total + 1);
    const nextDisplayIndex = candidates.reduce((nearest, candidate) => {
      return Math.abs(candidate - this.displayIndex) < Math.abs(nearest - this.displayIndex) ? candidate : nearest;
    }, candidates[0]);

    this.pendingCurrent = target;
    this.displayIndex = nextDisplayIndex;
    this.updateDots(target);
    this.updateVideoState();
    this.animateTo(this.displayIndex);
  }

  move(direction) {
    if (this.isAnimating || this.total <= 1) return;

    const nextIndex = mod(this.current + direction, this.total);
    let nextDisplayIndex = nextIndex + 1;

    if (direction > 0 && this.current === this.total - 1) {
      nextDisplayIndex = this.total + 1;
    } else if (direction < 0 && this.current === 0) {
      nextDisplayIndex = 0;
    }

    this.pendingCurrent = nextIndex;
    this.displayIndex = nextDisplayIndex;
    this.updateDots(nextIndex);
    this.updateVideoState();
    this.animateTo(nextDisplayIndex);
  }

  animateTo(displayIndex) {
    this.clearTransitionTimer();
    this.isAnimating = true;
    this.setTranslate(displayIndex);
    this.transitionTimer = window.setTimeout(() => this.finishAnimation(), this.getTransitionDuration() + 120);
  }

  finishAnimation() {
    this.clearTransitionTimer();
    this.isAnimating = false;

    if (this.pendingCurrent !== null) {
      this.current = this.pendingCurrent;
      this.pendingCurrent = null;
    }

    if (this.displayIndex === 0) {
      this.displayIndex = this.total;
      this.setTranslate(this.displayIndex, true);
    } else if (this.displayIndex === this.total + 1) {
      this.displayIndex = 1;
      this.setTranslate(this.displayIndex, true);
    }

    this.updateActiveState();
  }

  setTranslate(displayIndex, instant = false, dragOffset = 0) {
    const shouldDisableMotion = instant || this.reducedMotion.matches;

    if (this.noMotionFrame) {
      window.cancelAnimationFrame(this.noMotionFrame);
      this.noMotionFrame = null;
    }

    if (shouldDisableMotion) {
      this.section.classList.add('khls--no-motion');
    } else if (this.section.classList.contains('khls--no-motion')) {
      this.section.classList.remove('khls--no-motion');
      this.track.offsetHeight;
    }

    const offset = (-displayIndex * this.getSlideWidth()) + dragOffset;
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;

    if (shouldDisableMotion) {
      this.track.offsetHeight;
      this.noMotionFrame = window.requestAnimationFrame(() => {
        this.section.classList.remove('khls--no-motion');
        this.noMotionFrame = null;
      });
    }
  }

  getSlideWidth() {
    return Math.max(this.viewport.clientWidth, 1);
  }

  getTransitionDuration() {
    const styles = window.getComputedStyle(this.track);
    const duration = styles.transitionDuration.split(',')[0]?.trim() || '0s';

    if (duration.endsWith('ms')) return Number.parseFloat(duration) || 0;
    if (duration.endsWith('s')) return (Number.parseFloat(duration) || 0) * 1000;

    return Number.parseFloat(duration) || 0;
  }

  updateActiveState() {
    this.realSlides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.current;

      if (isActive) {
        slide.setAttribute(ACTIVE_ATTR, 'true');
      } else {
        slide.removeAttribute(ACTIVE_ATTR);
      }

      slide.setAttribute('aria-hidden', String(!isActive));
      this.setFocusable(slide, isActive);
    });

    this.slides.forEach((slide) => {
      if (slide.hasAttribute('data-khls-clone')) {
        slide.removeAttribute(ACTIVE_ATTR);
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.updateDots(this.current);

    this.updateVideoState();
  }

  updateDots(activeIndex) {
    this.dots.forEach((dot, dotIndex) => {
      if (dotIndex === activeIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  updateVideoState() {
    this.slides.forEach((slide, slideIndex) => {
      const shouldPlay = slideIndex === this.displayIndex && !document.hidden;

      slide.querySelectorAll('video').forEach((video) => {
        if (shouldPlay) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  setFocusable(slide, isActive) {
    slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
      if (isActive) {
        if (node.dataset.khlsTabindexPrevious !== undefined) {
          const previousValue = node.dataset.khlsTabindexPrevious;
          delete node.dataset.khlsTabindexPrevious;

          if (previousValue === '') {
            node.removeAttribute('tabindex');
          } else {
            node.setAttribute('tabindex', previousValue);
          }
        }

        return;
      }

      if (node.dataset.khlsTabindexPrevious === undefined) {
        node.dataset.khlsTabindexPrevious = node.getAttribute('tabindex') || '';
      }

      node.setAttribute('tabindex', '-1');
    });
  }

  clearTransitionTimer() {
    if (!this.transitionTimer) return;

    window.clearTimeout(this.transitionTimer);
    this.transitionTimer = null;
  }

  resetPointer() {
    this.pointerId = null;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.pointerDeltaX = 0;
    this.isDragging = false;
  }

  play() {
    if (!this.autoplay || this.total <= 1 || document.hidden || this.isDragging) return;
    if (this.timer) return;

    this.timer = window.setInterval(() => this.move(1), this.interval);
  }

  pause() {
    if (!this.timer) return;

    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.pause();
    this.play();
  }

  destroy() {
    this.pause();
    this.clearTransitionTimer();
    if (this.noMotionFrame) {
      window.cancelAnimationFrame(this.noMotionFrame);
      this.noMotionFrame = null;
    }
    this.abortController.abort();
    this.slides.forEach((slide) => {
      slide.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.track.querySelectorAll('[data-khls-clone]').forEach((clone) => clone.remove());
    controllers.delete(this.section);
  }
}

function initSection(section) {
  if (!section || section.dataset[INITIALIZED_KEY] === 'true') return;

  section.dataset[INITIALIZED_KEY] = 'true';
  const slideshow = new KompacHeroLoopSlideshow(section);
  slideshow.init();
}

function destroySection(section) {
  const controller = controllers.get(section);
  if (!controller) return;

  controller.destroy();
  delete section.dataset[INITIALIZED_KEY];
}

function init(root = document) {
  if (root.matches?.(SELECTOR)) initSection(root);
  root.querySelectorAll?.(SELECTOR).forEach(initSection);
}

init();

document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
  if (section) destroySection(section);
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-khls-slide]');
  const section = event.target.closest?.(SELECTOR);
  const controller = section ? controllers.get(section) : null;

  if (!slide || !controller || slide.hasAttribute('data-khls-clone')) return;

  controller.pause();
  controller.pendingCurrent = null;
  controller.current = toInteger(slide.dataset.khlsIndex, 0);
  controller.displayIndex = controller.current + (controller.total > 1 ? 1 : 0);
  controller.updateActiveState();
  controller.setTranslate(controller.displayIndex, true);
});

document.addEventListener('shopify:block:deselect', (event) => {
  const section = event.target.closest?.(SELECTOR);
  const controller = section ? controllers.get(section) : null;

  if (controller) controller.play();
});
